import Button from 'components/base/Button';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import { Form } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FormEvent, useState } from 'react';
import { resetPassword } from 'features/auth/authApi';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Validate token exists
  if (!token) {
    return (
      <AuthSimpleLayout>
        <div className="text-center mb-6">
          <h3 className="text-body-highlight text-danger">Invalid Reset Link</h3>
          <p className="text-body-secondary">The reset link is missing or expired.</p>
        </div>
        <div className="text-center">
          <Link to="/auth/forgot-password" className="btn btn-primary">
            Request New Reset Link
          </Link>
        </div>
      </AuthSimpleLayout>
    );
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setErrorMessage('');
    setSuccessMessage('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one lowercase letter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one digit');
      return;
    }

    if (!/[!@#$%^&*]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one special character (!@#$%^&*)');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({ token, newPassword });

      setSuccessMessage('Password has been reset successfully. Redirecting to login...');
      setNewPassword('');
      setConfirmPassword('');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSimpleLayout>
      <div className="text-center mb-6">
        <h3 className="text-body-highlight">Reset Password</h3>
        <p className="text-body-secondary">Enter your new password below</p>
      </div>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3 text-start">
          <Form.Label>New Password</Form.Label>
          <Form.Control
            type="password"
            required
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="New Password"
          />
          <Form.Text className="text-muted d-block mt-2">
            Password must contain:
            <ul className="mb-0 ps-3 mt-1">
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One digit</li>
              <li>One special character (!@#$%^&*)</li>
            </ul>
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type="password"
            required
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
          />
        </Form.Group>

        {errorMessage && (
          <div className="alert alert-danger py-2" role="alert">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success py-2" role="alert">
            {successMessage}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          className="w-100 mb-3"
          disabled={isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>

        <div className="text-center fs-9">
          <Link to="/auth/login">Back to Login</Link>
        </div>
      </Form>
    </AuthSimpleLayout>
  );
};

export default ResetPasswordPage;
